import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import StorefrontTwoTone from '@mui/icons-material/StorefrontTwoTone';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { headerBranchId } from '../food-delivery/helpers';
import { branchService } from '@/data/branchService';
import { foodDeliveryService } from '@/data/foodDeliveryService';
import {
  FoodIntegration,
  FOOD_PROVIDER_LABELS,
  OperationResult,
  RestaurantOpsView,
  RestaurantStatus,
  RESTAURANT_STATUS_COLOR,
  RESTAURANT_STATUS_LABELS
} from '@/types/FoodDelivery.interface';
import { Branch } from '@/types/Branch.interface';
import CategoryAvailabilityCard from './components/CategoryAvailabilityCard';

const STATUSES: RestaurantStatus[] = ['OPEN', 'BUSY', 'CLOSED'];

// dayOfWeek: 0=Pazar … 6=Cumartesi (backend WorkingHoursInterval ile birebir).
const DAY_LABELS = [
  'Pazar',
  'Pazartesi',
  'Salı',
  'Çarşamba',
  'Perşembe',
  'Cuma',
  'Cumartesi'
];

// UI satırı: her gün için açık/kapalı + açılış-kapanış (backend'e gönderirken
// yalnız "açık" günler interval'e çevrilir).
interface DayRow {
  enabled: boolean;
  opensAt: string;
  closesAt: string;
}

const emptyWeek = (): DayRow[] =>
  DAY_LABELS.map(() => ({
    enabled: false,
    opensAt: '09:00',
    closesAt: '23:00'
  }));

// view.workingHours (flat interval listesi) → 7 günlük UI satırına dönüştürür.
const weekFromView = (
  intervals?: { dayOfWeek: number; opensAt: string; closesAt: string }[]
): DayRow[] => {
  const week = emptyWeek();
  (intervals ?? []).forEach((iv) => {
    if (iv.dayOfWeek >= 0 && iv.dayOfWeek <= 6) {
      week[iv.dayOfWeek] = {
        enabled: true,
        opensAt: iv.opensAt,
        closesAt: iv.closesAt
      };
    }
  });
  return week;
};

const fmtDate = (iso?: string): string =>
  iso ? new Date(iso).toLocaleString('tr-TR') : '—';

// Push sonucunu (SUCCESS/SKIPPED/FAILED) kullanıcıya uygun toast'a çevirir.
const outcomeToast = (
  res: OperationResult
): { msg: string; severity: 'success' | 'error' | 'info' } => {
  if (res.outcome === 'SUCCESS') {
    return { msg: 'Platforma gönderildi.', severity: 'success' };
  }
  if (res.outcome === 'SKIPPED') {
    return {
      msg: `Atlandı (${res.reason ?? 'capability yok ya da kapalı'}).`,
      severity: 'info'
    };
  }
  return {
    msg: `Başarısız: ${res.reason ?? 'bilinmeyen hata'}.`,
    severity: 'error'
  };
};

const FoodDeliveryOperations: React.FC = () => {
  const { isBranchAdmin, isAdminView, currentBranch } = useUserViewMode();

  const [branches, setBranches] = useState<Branch[]>([]);
  const headerBranch = headerBranchId(currentBranch, isAdminView);
  const [branchId, setBranchId] = useState<number | null>(headerBranch);
  // Üst menüden şube değişince sayfa da o şubeye geçer.
  useEffect(() => {
    if (headerBranch) setBranchId(headerBranch);
  }, [headerBranch]);
  const [integrations, setIntegrations] = useState<FoodIntegration[]>([]);
  const [integrationId, setIntegrationId] = useState<number | null>(null);

  const [view, setView] = useState<RestaurantOpsView | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [minutes, setMinutes] = useState<string>('');
  const [week, setWeek] = useState<DayRow[]>(emptyWeek());
  const operationsRequest = useRef(0);
  const operationScope = useRef(0);
  const [toast, setToast] = useState<{
    msg: string;
    severity: 'success' | 'error' | 'info';
  } | null>(null);

  useEffect(() => {
    operationScope.current += 1;
    operationsRequest.current += 1;
    setBusy(false);
    setView(null);
    setToast(null);
    return () => {
      operationScope.current += 1;
      operationsRequest.current += 1;
    };
  }, [branchId, integrationId]);

  // Şube listesi (admin/şirket görünümü).
  useEffect(() => {
    if (isBranchAdmin) return;
    branchService
      .getAllFlat({ getAll: true })
      .then(setBranches)
      .catch(() =>
        setToast({ msg: 'Şubeler getirilemedi.', severity: 'error' })
      );
  }, [isBranchAdmin]);

  // Şube seçilince entegrasyonları getir.
  useEffect(() => {
    let active = true;
    setIntegrations([]);
    setIntegrationId(null);
    if (!branchId) return;
    foodDeliveryService
      .listByBranch(branchId)
      .then((list) => {
        if (!active) return;
        setIntegrations(list);
        setIntegrationId(list[0]?.id ?? null);
      })
      .catch(() => {
        if (!active) return;
        setToast({ msg: 'Entegrasyonlar getirilemedi.', severity: 'error' });
      });
    return () => {
      active = false;
    };
  }, [branchId]);

  const loadData = useCallback(async () => {
    const request = ++operationsRequest.current;
    if (!integrationId) {
      setView(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const v = await foodDeliveryService.getOperations(integrationId);
      if (request !== operationsRequest.current) return;
      setView(v);
      setMinutes(
        v.deliveryDurationMinutes != null
          ? String(v.deliveryDurationMinutes)
          : ''
      );
      setWeek(weekFromView(v.workingHours));
    } catch {
      if (request !== operationsRequest.current) return;
      setToast({ msg: 'Operasyon durumu getirilemedi.', severity: 'error' });
    } finally {
      if (request === operationsRequest.current) setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    loadData();
    return () => {
      operationsRequest.current += 1;
    };
  }, [loadData]);

  const handleStatus = async (status: RestaurantStatus) => {
    if (!integrationId) return;
    const scope = operationScope.current;
    setBusy(true);
    try {
      const res = await foodDeliveryService.setRestaurantStatus(
        integrationId,
        status
      );
      if (scope !== operationScope.current) return;
      setToast(outcomeToast(res));
      await loadData();
    } catch {
      if (scope !== operationScope.current) return;
      setToast({ msg: 'İşlem başarısız.', severity: 'error' });
    } finally {
      if (scope === operationScope.current) setBusy(false);
    }
  };

  const handleDuration = async () => {
    if (!integrationId) return;
    const m = Number(minutes);
    if (!Number.isInteger(m) || m < 1 || m > 240) {
      setToast({ msg: 'Süre 1–240 dakika olmalı.', severity: 'error' });
      return;
    }
    const scope = operationScope.current;
    setBusy(true);
    try {
      const res = await foodDeliveryService.setDeliveryDuration(
        integrationId,
        m
      );
      if (scope !== operationScope.current) return;
      setToast(outcomeToast(res));
      await loadData();
    } catch {
      if (scope !== operationScope.current) return;
      setToast({ msg: 'İşlem başarısız.', severity: 'error' });
    } finally {
      if (scope === operationScope.current) setBusy(false);
    }
  };

  const setDay = (idx: number, patch: Partial<DayRow>) =>
    setWeek((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));

  const handleWorkingHours = async () => {
    if (!integrationId) return;
    // Açık günlerde kapanış > açılış olmalı (gece yarısını aşan saat MVP dışı).
    const invalid = week.some((d) => d.enabled && d.closesAt <= d.opensAt);
    if (invalid) {
      setToast({
        msg: 'Açık günlerde kapanış saati açılıştan sonra olmalı.',
        severity: 'error'
      });
      return;
    }
    const intervals = week
      .map((d, dayOfWeek) => ({ ...d, dayOfWeek }))
      .filter((d) => d.enabled)
      .map((d) => ({
        dayOfWeek: d.dayOfWeek,
        opensAt: d.opensAt,
        closesAt: d.closesAt
      }));
    if (intervals.length === 0) {
      setToast({ msg: 'En az bir gün açık olmalı.', severity: 'error' });
      return;
    }
    const scope = operationScope.current;
    setBusy(true);
    try {
      const res = await foodDeliveryService.setWorkingHours(
        integrationId,
        intervals
      );
      if (scope !== operationScope.current) return;
      setToast(outcomeToast(res));
      await loadData();
    } catch {
      if (scope !== operationScope.current) return;
      setToast({ msg: 'İşlem başarısız.', severity: 'error' });
    } finally {
      if (scope === operationScope.current) setBusy(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageTitleWrapper>
        <Box>
          <Typography variant="h3" component="h3" gutterBottom>
            Restoran Operasyonları
          </Typography>
          <Typography variant="subtitle2">
            Şubenizin platform tarafındaki açık/kapalı/yoğun durumunu ve
            teslimat süresini yönetin. Yalnız platformun desteklediği işlemler
            gönderilir.
          </Typography>
        </Box>
      </PageTitleWrapper>

      <Box sx={{ px: 4, pb: 4 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ md: 'center' }}
            >
              {!isBranchAdmin && (
                <Autocomplete
                  sx={{ minWidth: 280 }}
                  options={branches}
                  getOptionLabel={(b) => b.name}
                  value={branches.find((b) => b.id === branchId) ?? null}
                  onChange={(_e, val) => setBranchId(val?.id ?? null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Şube seç" />
                  )}
                />
              )}
              <Autocomplete
                sx={{ minWidth: 280 }}
                options={integrations}
                getOptionLabel={(i) =>
                  `${FOOD_PROVIDER_LABELS[i.providerCode]}${
                    i.displayName ? ` — ${i.displayName}` : ''
                  }`
                }
                value={integrations.find((i) => i.id === integrationId) ?? null}
                onChange={(_e, val) => setIntegrationId(val?.id ?? null)}
                disabled={!branchId}
                renderInput={(params) => (
                  <TextField {...params} label="Entegrasyon seç" />
                )}
              />
            </Stack>
          </CardContent>
        </Card>

        {!integrationId ? (
          <Alert severity="info">Bir şube ve entegrasyon seçin.</Alert>
        ) : loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : !view?.supported ? (
          <Alert severity="warning">
            Bu platform restoran operasyonlarını (açık/kapalı/yoğun)
            desteklemiyor.
          </Alert>
        ) : (
          <Card>
            <CardContent>
              {!view.opsEnabled && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Operasyon push&apos;u bu entegrasyonda kapalı (rollback
                  bayrağı). Yapılan işlemler platforma gönderilmez.
                </Alert>
              )}

              {/* Restoran durumu */}
              <Typography variant="h4" gutterBottom>
                Restoran Durumu
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
                sx={{ mb: 1 }}
              >
                <Typography variant="body2" color="textSecondary">
                  Mevcut:
                </Typography>
                <Chip
                  color={
                    view.status
                      ? RESTAURANT_STATUS_COLOR[view.status]
                      : 'default'
                  }
                  label={
                    view.status
                      ? RESTAURANT_STATUS_LABELS[view.status]
                      : 'Bilinmiyor'
                  }
                />
                <Typography variant="caption" color="textSecondary">
                  Son değişiklik: {fmtDate(view.statusUpdatedAt)}
                </Typography>
              </Stack>

              {/* Platformdaki gerçek durum panelden farklı olabilir: mağaza
                  satıcı panelinden kapatılmış ya da çalışma saati dışına
                  düşmüş olabilir. Bu fark gizlenirse "sipariş gelmiyor"
                  şikâyetinin sebebi görünmez. */}
              {view.platformStatusMismatch && view.platformStatus && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Platformdaki gerçek durum{' '}
                  <strong>
                    {RESTAURANT_STATUS_LABELS[view.platformStatus]}
                  </strong>
                  . Mağaza satıcı panelinden kapatılmış ya da çalışma saatleri
                  dışında olabilir — bu durumda sipariş gelmez.
                </Alert>
              )}
              {view.platformStatusError && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Platformdaki durum doğrulanamadı ({view.platformStatusError}).
                  Yukarıdaki değer en son bilinen durumdur.
                </Alert>
              )}
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3 }}>
                {STATUSES.map((s) => (
                  <Button
                    key={s}
                    variant={view.status === s ? 'contained' : 'outlined'}
                    color={
                      RESTAURANT_STATUS_COLOR[s] === 'default'
                        ? 'inherit'
                        : RESTAURANT_STATUS_COLOR[s]
                    }
                    startIcon={<StorefrontTwoTone />}
                    disabled={busy}
                    onClick={() => handleStatus(s)}
                  >
                    {RESTAURANT_STATUS_LABELS[s]}
                  </Button>
                ))}
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Teslimat süresi */}
              <Typography variant="h4" gutterBottom>
                Teslimat Süresi
              </Typography>
              {!view.deliveryDurationSupported ? (
                <Alert severity="info">
                  Bu platform teslimat süresi güncellemeyi desteklemiyor.
                </Alert>
              ) : (
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <TextField
                    label="Dakika"
                    type="number"
                    size="small"
                    value={minutes}
                    onChange={(e) => setMinutes(e.target.value)}
                    inputProps={{ min: 1, max: 240 }}
                    sx={{ width: 140 }}
                  />
                  <Button
                    variant="contained"
                    disabled={busy}
                    onClick={handleDuration}
                  >
                    Gönder
                  </Button>
                  <Typography variant="caption" color="textSecondary">
                    Son güncelleme: {fmtDate(view.deliveryDurationUpdatedAt)}
                  </Typography>
                </Stack>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Çalışma saatleri */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="h4">Çalışma Saatleri</Typography>
                <Typography variant="caption" color="textSecondary">
                  Son güncelleme: {fmtDate(view.workingHoursUpdatedAt)}
                </Typography>
              </Stack>
              {!view.workingHoursSupported ? (
                <Alert severity="info">
                  Bu platform çalışma saati güncellemeyi desteklemiyor.
                </Alert>
              ) : (
                <>
                  <Stack spacing={1.5} sx={{ mb: 2 }}>
                    {week.map((d, idx) => (
                      <Stack
                        key={idx}
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <FormControlLabel
                          sx={{ minWidth: 150 }}
                          control={
                            <Switch
                              checked={d.enabled}
                              onChange={(e) =>
                                setDay(idx, { enabled: e.target.checked })
                              }
                            />
                          }
                          label={DAY_LABELS[idx]}
                        />
                        <TextField
                          label="Açılış"
                          type="time"
                          size="small"
                          value={d.opensAt}
                          disabled={!d.enabled}
                          onChange={(e) =>
                            setDay(idx, { opensAt: e.target.value })
                          }
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 130 }}
                        />
                        <TextField
                          label="Kapanış"
                          type="time"
                          size="small"
                          value={d.closesAt}
                          disabled={!d.enabled}
                          onChange={(e) =>
                            setDay(idx, { closesAt: e.target.value })
                          }
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 130 }}
                        />
                        {!d.enabled && <Chip size="small" label="Kapalı" />}
                      </Stack>
                    ))}
                  </Stack>
                  <Button
                    variant="contained"
                    disabled={busy}
                    onClick={handleWorkingHours}
                  >
                    Çalışma Saatlerini Gönder
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {integrationId && (
          <CategoryAvailabilityCard
            integrationId={integrationId}
            onMessage={(msg, severity) => setToast({ msg, severity })}
          />
        )}
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default FoodDeliveryOperations;
